import mitt, { type EventType } from 'mitt'
import type { EventName } from '@/plugins/cad/module/core/common/event'
import { StartMode } from '@/plugins/cad/module/core/common/interface'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import { ToolStack } from '@/plugins/cad/module/core/tool/ToolStack'

/**
 * CAD 控制器抽象基类，负责维护工具栈、分发事件和默认工具。
 */
export abstract class Controller {
  protected readonly emitter = mitt()
  /** 当前控制器持有的工具栈 */
  readonly toolStack = new ToolStack(this.emitter)

  /** 控制器的默认工具，退栈后回落到此工具 */
  declare defaultTool: BaseTool | undefined

  /**
   * 启动控制器并初始化默认工具。
   *
   * @param args - 透传给子类 initialize 的参数
   */
  start(...args: any[]) {
    this.initialize(...args)

    if (this.defaultTool) {
      this.toolStack.defaultTool = this.defaultTool
      this.defaultTool.start(this, StartMode.none)
    }
  }

  /** 退出控制器并清空工具栈 */
  exit() {
    this.terminate()
    this.toolStack.clear()
  }

  // 初始化-进入服务后需要做的都放在这处理
  protected abstract initialize(...args: any[]): void

  // 终止-退出服务后需要做的都放在这处理
  protected abstract terminate(): void

  /**
   * 订阅控制器事件。
   *
   * @param type - 事件名称
   * @param handler - 事件触发时调用的回调
   */
  on(type: EventName | EventType, handler: (...args) => void) {
    this.emitter.on(type, handler)
  }

  /**
   * 取消订阅控制器事件。
   *
   * @param type - 事件名称
   * @param handler - 需要移除的回调，省略时清除该事件的所有订阅
   */
  off(type: EventName | EventType, handler?: (...args) => void) {
    this.emitter.off(type, handler)
  }

  /**
   * 获取当前应当接收事件的工具。
   *
   * @returns 栈顶工具；栈为空时回退到默认工具
   */
  getTool() {
    return this.toolStack.top || this.defaultTool
  }

  // 事件派发到当前工具
  protected eventDispatch(type: EventName, ...args: any[]) {
    const tool = this.getTool()
    if (!tool) {
      return
    }

    // 调用注入的事件处理器
    tool.injectedMap[type]?.forEach((func) => func(...args))

    // 直接调用工具对应方法
    tool[type]?.(...args)
  }
}
